import React, { useEffect, useState, useMemo } from "react";
import {
  EventPayload,
  FollowerNode,
  ChannelEvents,
} from "@local-logic/channel";
import {
  useSearch,
  useNavigate,
  MakeGenerics,
  useLocation,
} from "@tanstack/react-location";
import once from "lodash.once";
import { primitiveUtils } from "@local-logic/core/utils";
import useEffectOnce from "~/hooks/useEffectOnce";
import useDebounce from "~/hooks/useDebounce";
import { SDKProps, DefaultContext } from "~/types";
import { SDKs } from "~/constants/routes";
import errorTracker from "~/utils/errorTracker";
import defaultState from "./defaultState";
import i18n from "../../i18n";

// Using once to get around generic Context issue: https://stackoverflow.com/a/61020816/4282290
const createStateManagerContext = once(<T extends SDKProps>() =>
  React.createContext<DefaultContext<T>>({} as DefaultContext<T>)
);

const useStateManagerContext = <T extends SDKProps>() => {
  const context = React.useContext(createStateManagerContext<T>());

  if (!context) {
    throw new Error(
      "useStateManagerContext must be used under StateManagerProvider"
    );
  }

  return context;
};

const StateManagerProvider = React.memo(
  <T extends SDKProps>({ children }: { children: React.ReactNode }) => {
    const StateManagerContext = createStateManagerContext<T>();
    const search = useSearch<
      MakeGenerics<{
        Search: DefaultContext<T>["sdkState"] & { channelId: string };
      }>
    >() as DefaultContext<T>["sdkState"] & { channelId: string };
    const sdkStateRef = React.useRef(search) as React.MutableRefObject<
      DefaultContext<T>["sdkState"]
    >;
    const location = useLocation();
    const pathSegments = location.current.pathname.split("/");
    const currentRoute = pathSegments[pathSegments.length - 1] as SDKs;
    const defaultSDKState = defaultState[
      currentRoute
    ] as DefaultContext<T>["sdkState"];

    const followerNodeRef = React.useRef<FollowerNode>();
    const [isConnected, setIsConnected] = useState(false);
    const [sdkState, _setSdkState] =
      useState<DefaultContext<T>["sdkState"]>(defaultSDKState);

    const debouncedSdkState = useDebounce(sdkState, 500);
    const navigate = useNavigate();

    const setSdkState = (data: DefaultContext<T>["sdkState"]) => {
      sdkStateRef.current = data;
      _setSdkState(data);
    };

    const handleMessage = (e: EventPayload) => {
      if (e.name === ChannelEvents.GET_STATE) {
        followerNodeRef.current?.message(
          ChannelEvents.GET_STATE_RESPONSE,
          sdkStateRef.current,
          e.eventId
        );
      }

      if (e.name === ChannelEvents.SET_STATE) {
        setSdkState(e.data as DefaultContext<T>["sdkState"]);
      }
    };

    /**
     * The `.connect()` event should only every be triggered once.
     */
    useEffectOnce(() => {
      const followerNode = new FollowerNode(search.channelId, {
        targetOrigin: document.referrer,
        window: window.parent,
      });

      followerNodeRef.current = followerNode;

      followerNode?.onMessage(handleMessage);

      followerNode?.connect().then((resp) => {
        const mergedState = primitiveUtils.object.mergeDeep(
          sdkState,
          resp.data as DefaultContext<T>["sdkState"]
        );
        setSdkState(mergedState);
        setIsConnected(true);

        errorTracker.addBreadCrumb(
          "stateManager",
          "useEffectOnce: Connected to sdks-js",
          {
            oldState: sdkState,
            newState: resp.data,
            mergedState,
          }
        );
      });
    });

    const onChange: DefaultContext<T>["onChange"] = (e: unknown) => {
      followerNodeRef.current?.message(ChannelEvents.ON_CHANGE, e);

      errorTracker.addBreadCrumb("stateManager", "onChange", {
        data: e,
      });
    };

    useEffect(() => {
      navigate({
        search: () => debouncedSdkState,
        replace: true,
      });
    }, [debouncedSdkState]);

    useEffect(() => {
      if (sdkState.options) {
        onChange({ type: "options", data: sdkState.options });
      }
    }, [sdkState.options]);

    const contextValue = useMemo(
      () => ({
        sdkState,
        setSdkState,
        onChange,
        followerNode: followerNodeRef.current,
      }),
      [sdkState, setSdkState, onChange]
    );

    useEffect(() => {
      // Avoids unecessarily setting the language multiple times
      if (!isConnected || !sdkState.globalOptions?.locale) {
        return;
      }

      const language = sdkState.globalOptions?.locale || "en";

      if (i18n.language !== language) {
        errorTracker.addBreadCrumb("stateManager", "i18n.changeLanguage", {
          language,
        });

        i18n.changeLanguage(language);
      }
    }, [sdkState.globalOptions?.locale, isConnected]);

    if (!isConnected) {
      return null;
    }

    return (
      <StateManagerContext.Provider value={contextValue}>
        {children}
      </StateManagerContext.Provider>
    );
  }
);

StateManagerProvider.displayName = "StateManagerProvider";

export { useStateManagerContext, StateManagerProvider };
